// src/components/admin/Orders/OrderDetails.tsx
import React, { useEffect, useState } from "react";
import { Row, Button, Descriptions, Tag, Col, Typography, Layout, Tooltip } from "antd";
import { Link, useParams, useNavigate } from "react-router-dom";
import { withFirebase } from "../../dev/Firebase/Firebase";
import styled from "styled-components";
import { Helmet } from "react-helmet-async";
import { OrderStatus } from "../../../constants/OrderStatusEnum";
import Spinner from "../../dev/utils/Spinner";
import { toast } from "react-toastify";
import { ArrowLeftOutlined } from "@ant-design/icons";

const { Title } = Typography;
const { Content } = Layout;

const PaddingDiv = styled.div`
  margin: 24px;
  padding: 24px;
  background-color: #fff;
  box-shadow: 0 4px 15px rgba(255, 204, 0, 0.2);
  border-radius: 8px;
`;

interface OrderInfo {
  id: string;
  type: string;
  price: number;
  reserveAmount: number;
  status: string;
  uid: string;
}

interface OrderDetailsProps {
  firebase: any; // Replace with FirebaseContextType
}

const OrderDetails: React.FC<OrderDetailsProps> = ({ firebase }) => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [orderInfo, setOrderInfo] = useState<OrderInfo>();

  useEffect(() => {
    const unsubscribe = firebase.order(orderId!).onSnapshot((orderDoc: any) => {
      const orderInfo = { id: orderDoc.id, ...orderDoc.data() };
      setOrderInfo(orderInfo);
    });

    return () => unsubscribe();
  }, [firebase, orderId]);

  const { id, type, price, reserveAmount, status, uid } = orderInfo || {};

  const approveOrder = () => {
    if (status !== OrderStatus.COMPLETED) {
      firebase.order(orderId!).update({
        status: OrderStatus.RESERVED,
      });
    } else {
      toast.error("Order already paid");
    }
  };

  const declineOrder = () => {
    if (status !== OrderStatus.COMPLETED) {
      firebase.order(orderId!).update({
        status: OrderStatus.DECLINED,
      });
    } else {
      toast.error("Order already paid");
    }
  };

  return orderInfo ? (
    <PaddingDiv>
      <Helmet>
        <title>Order Details - One Light System OLS Admin</title>
        <meta
          name="description"
          content="View and manage order details in the One Light System OLS admin panel for Sun Light Meditation education."
        />
        <meta
          name="keywords"
          content="OLS Order Details, Sun Light Meditation, OLS Admin, Light Civilization"
        />
        <link rel="canonical" href={`https://www.olsme.com/admin/orders/${orderId}`} />
      </Helmet>
      <Content>
        <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
          <Button
            aria-label="Go back to orders list"
            onClick={() => navigate("/admin/orders")}
            style={{ marginRight: "16px", color: "#0427f4" }}
            icon={<ArrowLeftOutlined />}
          >
            Back
          </Button>
          <Title level={3} style={{ color: "#0427f4" }}>
            Order Info
          </Title>
        </div>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <Descriptions bordered column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 1 }}>
              <Descriptions.Item label="Order">#{id}</Descriptions.Item>
              <Descriptions.Item label="Status">
                {status ? (
                  status === OrderStatus.RESERVED ? (
                    <Tag color="cyan">Reserved</Tag>
                  ) : status === OrderStatus.PENDING ? (
                    <Tag color="blue">Pending</Tag>
                  ) : status === OrderStatus.DECLINED ? (
                    <Tag color="red">Canceled</Tag>
                  ) : (
                    status === OrderStatus.COMPLETED && <Tag color="green">Paid</Tag>
                  )
                ) : (
                  <Tag color="red">No Status</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Asset">{type}</Descriptions.Item>
              <Descriptions.Item label="Reserve Amount">{reserveAmount}</Descriptions.Item>
              <Descriptions.Item label="Price">{price}</Descriptions.Item>
              <Descriptions.Item label="User Info">
                <Link to={`/admin/user/${uid}`} aria-label={`View user ${uid}`}>
                  Link
                </Link>
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
        {status && status === OrderStatus.PENDING && (
          <Row style={{ marginTop: "24px" }} gutter={[16, 16]}>
            <Col>
              <Tooltip title="Approve this order">
                <Button
                  style={{ marginRight: "20px", background: "#ffaa00", color: "#fff" }}
                  type="primary"
                  onClick={approveOrder}
                  aria-label="Approve order"
                >
                  Approve Order
                </Button>
              </Tooltip>
            </Col>
            <Col>
              <Tooltip title="Decline this order">
                <Button
                  style={{ background: "#ff8800", color: "#fff" }}
                  type="danger"
                  onClick={declineOrder}
                  aria-label="Decline order"
                >
                  Decline Order
                </Button>
              </Tooltip>
            </Col>
          </Row>
        )}
      </Content>
    </PaddingDiv>
  ) : (
    <Spinner />
  );
};

export default withFirebase(OrderDetails);